import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, Observable, tap } from 'rxjs';
import { GetAnswerRequest } from '../../../../../backend/src/core/openai/openai.gate';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { OpenaiModel } from '../openai/openai';
import { OpenaiService } from '../openai/openai.service';
import { PostHogEvent } from '../posthog/posthog.event';
import {
  getCheckValidProductFunctions,
  getCheckValidProductPrompt,
  IsValidProductResponse,
} from '../prompt/products/check-valid-product.prompt';
import { getProductTitlePrompt } from '../prompt/products/get-product-title.prompt';
import { Product, ProductUnsafeSubData } from './product';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private _changeSubject = new BehaviorSubject<Product | undefined>(undefined);

  constructor(
    private coreItemService: CoreItemService,
    private openAiService: OpenaiService
  ) {}

  verifyProductDescription$(productDescription: string): Observable<IsValidProductResponse> {
    const answerPrompt: GetAnswerRequest = {
      messageList: [{ role: 'user', content: getCheckValidProductPrompt(productDescription) }],
      config: {
        openaiModel: OpenaiModel.gpt4,
        functions: getCheckValidProductFunctions(),
      },
    };

    return this.openAiService.getAnswer$(answerPrompt);
  }

  getProductById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Product));
  }

  productList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({ coreTypeList: [CoreItemType.product] }).pipe(
          map((list) => {
            list.sort((a, b) =>
              (typeof a?.title === 'string' ? a?.title : '').localeCompare(typeof b?.title === 'string' ? b?.title : '')
            );
            return list as Product[];
          })
        );
      })
    );
  }

  createProductByDescription$(productDescription: string, options?: { productName?: string }) {
    if (!options?.productName) {
      return this.openAiService
        .getAnswer$({
          messageList: [{ role: 'user', content: getProductTitlePrompt(productDescription) }],
          config: { openaiModel: OpenaiModel.gpt4 },
        })
        .pipe(
          mergeMap((result) => {
            return this.addProduct$(result, productDescription, {});
          })
        );
    } else {
      return this.addProduct$(options.productName, productDescription, {});
    }
  }

  updateProduct$(product: Product) {
    posthog.capture(PostHogEvent.productUpdate);
    return this.coreItemService.updateCoreItem$(product);
  }

  addProduct$(title: string, description: string, unsafeSubData: ProductUnsafeSubData) {
    posthog.capture(PostHogEvent.productAdd);
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            description,
            coreType: CoreItemType.product,
            unsafeSubData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as Product);
          return arr[0] as Product;
        })
      );
  }

  deleteProductList$(productList: Product[]) {
    const deleteMetadataList = productList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.productDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }
}
